import { Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ITimesheetEntry } from '@core/interfaces';

import {
  approvedLeaveLabel,
  attendanceSource,
  kekaDayTypeLabel,
  kekaDayTypeOutline,
  kekaHoursBackground,
  kekaHoursTextColor,
} from '@shared/utils/timesheet.utils';

@Component({
  selector: 'app-timesheet-row-cell',
  template: `
    @if (isEditing()) {
      <mat-form-field class="w-20" subscriptSizing="dynamic">
        <input
          matInput
          type="number"
          [value]="hours()"
          (input)="onHoursInput($event)"
          [class.border-red-500]="hasError()"
          [class.border-2]="hasError()"
          class="text-center"
          min="0"
          max="24"
          step="0.5"
        />
      </mat-form-field>
    } @else {
      @if (entry(); as e) {
        @if (showAttendanceSource()) {
          <div class="flex w-full min-w-20 items-center gap-1 py-2">
            <span
              class="size-7 shrink-0 rounded-full border-2 border-solid border-(--mat-sys-outline-variant) bg-(--mat-sys-surface) inline-flex items-center justify-center text-xs font-semibold"
              [style.background]="kekaHoursBackground(e.day_type, e.leave_status, e.leave_session)"
              [style.border-color]="kekaDayTypeOutline(e.day_type)"
              [class.text-(--mat-sys-error)]="hasError()"
              [style.color]="
                !hasError() ? kekaHoursTextColor(e.day_type, e.leave_status, e.leave_session) : null
              "
              [matTooltip]="kekaHoursLabel(e)"
              [attr.aria-label]="kekaHoursLabel(e)"
              role="img"
              tabindex="0"
            >
              {{ roundedHours(e.submitted_hours) }}
            </span>
            @if (e.attendance_log_source !== null && e.attendance_log_source !== undefined) {
              <mat-icon
                class="size-5! shrink-0 inline-flex! items-center justify-center leading-none! text-base!"
                [matTooltip]="'Login source: ' + attendanceSource(e.attendance_log_source).label"
                [attr.aria-label]="
                  'Login source: ' + attendanceSource(e.attendance_log_source).label
                "
                tabindex="0"
                >{{ attendanceSource(e.attendance_log_source).icon }}</mat-icon
              >
            }
          </div>
        } @else {
          <span class="inline-flex items-center gap-1">
            @if (e.is_modified) {
              <span
                class="cursor-help"
                [matTooltip]="
                  'Extracted: ' + e.extracted_hours + ' | Submitted: ' + e.submitted_hours
                "
              >
                <span class="text-gray-400 line-through text-sm">{{ e.extracted_hours }}</span>
                <span [class.text-red-600]="hasError()">{{ e.submitted_hours }}</span>
              </span>
            } @else {
              <span [class.text-red-600]="hasError()" [class.text-green-600]="isWithinTrusted()">
                {{ e.submitted_hours }}
              </span>
            }
          </span>
        }
      } @else {
        <span class="text-gray-400">--</span>
      }
    }
  `,
  imports: [FormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatTooltipModule],
})
export class TimesheetRowCellComponent {
  entry = input<ITimesheetEntry | undefined>();
  weekDays = input<{ day: string; date: string; label: string }[]>();
  isEditing = input<boolean>(false);
  hasError = input<boolean>(false);
  isWithinTrusted = input<boolean>(false);
  showAttendanceSource = input<boolean>(false);
  readonly attendanceSource = attendanceSource;
  readonly kekaDayTypeOutline = kekaDayTypeOutline;
  readonly kekaHoursBackground = kekaHoursBackground;
  readonly kekaHoursTextColor = kekaHoursTextColor;

  hoursChanged = output<number>();

  hours = computed(() => this.entry()?.submitted_hours ?? 0);

  kekaHoursLabel(entry: ITimesheetEntry): string {
    const hours = entry.is_modified
      ? `Extracted: ${entry.extracted_hours} | Submitted: ${entry.submitted_hours}`
      : `${entry.submitted_hours} hours of 9-hour target`;
    return `${hours}${kekaDayTypeLabel(entry.day_type)}${approvedLeaveLabel(
      entry.leave_status,
      entry.leave_session,
    )}`;
  }

  roundedHours(hours: number): number {
    return Math.round(hours);
  }

  onHoursInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value) || 0;
    this.hoursChanged.emit(value);
  }
}
